<template>
    <USlideover prevent-close :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-xl' }">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Bulk Activation</h3>
                </div>
            </template>

            <div v-if="loading" class="h-full flex flex-col items-start justify-center space-y-2 p-8 sm:p-24">
                <div class="w-full flex items-center">
                    <div class="flex items-center space-x-2.5">
                        <p class="text-sm font-medium text-gray-700">Data is being processed...</p>
                    </div>
                </div>
                <UProgress animation="carousel" />
                <div class="flex items-center space-x-2 !mt-3">
                    <UIcon name="i-ph-warning-duotone" class="size-4 text-orange-600" />
                    <p class="text-xs font-medium text-orange-600">Please do not close this window!</p>
                </div>
            </div>
            <div v-else-if="completed" class="h-full flex flex-col">
                <div class="h-full flex flex-col items-start justify-center space-y-2 p-8 sm:p-24">
                    <h2 class="text-lg font-medium text-gray-700">
                        Process completed successfully!
                    </h2>
                    <div class="w-full h-px bg-gray-200 !my-2"></div>
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-check-circle-duotone" class="size-5 text-green-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.total - importState.existing - importState.invalid }}</span> new customers activated
                        </p>
                    </div>
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-check-circle-duotone" class="size-5 text-amber-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.existing }}</span> existing customers activated
                        </p>
                    </div>
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-x-circle-duotone" class="size-5 text-red-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.invalid }}</span> invalid customers provided
                        </p>
                    </div>
                    <div v-if="importState.families !== undefined" class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-users-three-duotone" class="size-5 text-blue-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.families }}</span> families linked,
                            <span class="font-mono font-semibold">{{ importState.dependents }}</span> dependents activated
                        </p>
                    </div>
                </div>

                <div v-if="importState.errors.length" class="bg-red-50 border-t border-red-200 py-4 px-5 rounded-md min-h-max max-h-[400px] overflow-auto">
                    <h3 class="text-sm font-medium">There were some rows with invalid data:</h3>
                    <ul class="list-disc ps-6 mt-2">
                        <li v-for="error in importState.errors" class="text-xs">
                            <strong v-if="error.index !== undefined">Row <span class="font-mono">{{ error.index + 1 }}</span>:</strong>
                            {{ error.message }}
                        </li>
                    </ul>
                </div>
            </div>
            <div v-else class="space-y-6">
                <UFormGroup v-if="!userPartnerId" name="partner_id" label="Partner" required>
                    <UInputMenu
                        v-model="partner.selected"
                        @update:model-value="state.partner_id = $event.id"
                        :loading="partner.searching"
                        :search="searchPartner"
                        placeholder="Choose a partner..."
                        option-attribute="name"
                        size="xl"
                        trailing
                        by="id"
                    />
                </UFormGroup>
                <UFormGroup v-if="partner.selected" name="name" label="Name">
                    <UInput
                        v-model="state.name"
                        placeholder="e.g. March 2026 Employees"
                        size="xl"
                    />
                </UFormGroup>
                <template v-if="partner.selected">
                    <UFormGroup v-if="!quickActivationPartner" name="identity_mode" label="Identity">
                        <URadioGroup
                            v-model="identityMode"
                            :options="[
                                { value: 'phone', label: 'Phone number' },
                                { value: 'email', label: 'Email (no phone)' },
                                { value: 'family', label: 'Family policies' },
                            ]"
                            :ui="{ fieldset: 'flex gap-6' }"
                        />
                        <template #help>
                            <span v-if="identityMode === 'email'" class="text-xs text-gray-500">
                                Beneficiaries get a synthetic email from <span class="font-mono">{{ EMAIL_ANCHOR_FIELD }}</span>. No message is sent.
                            </span>
                            <span v-else-if="identityMode === 'family'" class="text-xs text-gray-500">
                                Rows sharing a policy holder become one family. Members with a phone are invited as users; members without a phone become dependents of the family head.
                            </span>
                        </template>
                    </UFormGroup>
                    <UAlert
                        v-if="identityMode === 'family'"
                        title="Family policies only"
                        icon="i-ph-warning-duotone"
                        color="amber"
                        variant="subtle"
                    >
                        <template #description>
                            <p>
                                Only upload policies where the <span class="font-mono font-semibold">policy_holder</span> is the family head (the father or mother bound to the policy).
                                Never use this mode for corporate policies, where the policy holder is a company: every member of that company would be merged into a single family.
                            </p>
                            <UCheckbox
                                v-model="familyConfirmed"
                                name="family_confirmed"
                                label="I confirm every row in this file belongs to a family policy"
                                class="mt-3"
                            />
                        </template>
                    </UAlert>
                    <div>
                        <p class="text-sm text-gray-500">
                            Upload a <b>CSV</b> file with the table headers as the first row, making sure they match exactly as shown below:
                        </p>
                        <div class="mt-1 mb-3 flex flex-wrap gap-1">
                            <UBadge
                                v-for="field in subscriptionFields"
                                :key="field.key"
                                :label="field.key"
                                color="gray"
                                variant="subtle"
                                class="font-mono font-semibold"
                            />
                        </div>
                        <div class="mt-4 space-y-2">
                            <p class="text-sm text-gray-500 font-semibold">Requirements</p>
                            <ul v-if="identityMode === 'email'" class="text-sm list-disc ps-6 space-y-1">
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">birth_year</span> should be a 4-digit year (eg. <span class="font-medium">1985</span>); it is also the login password</li>
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">{{ EMAIL_ANCHOR_FIELD }}</span> must be unique per beneficiary (used to build the email)</li>
                                <li>No <span class="font-medium">phone</span> or <span class="font-medium">birth_date</span> column needed</li>
                            </ul>
                            <ul v-else-if="identityMode === 'family'" class="text-sm list-disc ps-6 space-y-1">
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">policy_holder</span> is the family head's name, identical on every row of the family</li>
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">phone_number</span> is optional; when present it should be in international format (eg. <span class="font-medium">9613123456</span>)</li>
                                <li>The member with a phone whose name matches the policy holder becomes the family head, otherwise the first member with a phone</li>
                                <li>Families where nobody has a phone number are rejected</li>
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">birth_date</span> should be formatted as <span class="font-medium">YYYY-MM-DD</span></li>
                            </ul>
                            <ul v-else class="text-sm list-disc ps-6 space-y-1">
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">phone_number</span> should be in international format (eg. <span class="font-medium">9613123456</span>)</li>
                                <li><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">birth_date</span> should be formatted as <span class="font-medium">YYYY-MM-DD</span></li>
                                <li v-if="selfService"><span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">expiry_date</span> is when the membership ends, formatted as <span class="font-medium">YYYY-MM-DD</span> and in the future</li>
                            </ul>
                        </div>
                    </div>
                    <div class="rounded-md border border-dashed border-strong border-gray-300">
                        <label v-if="!state.file" class="h-36 flex items-center justify-center space-x-1 cursor-pointer">
                            <UIcon name="i-ph-file-text-duotone" class="size-4" />
                            <p class="text-sm">Choose a <span class="text-primary">file</span> to import</p>
                            <input @change="onChooseFile" ref="fileInput" type="file" accept="text/csv" class="sr-only" />
                        </label>
                        <div v-else class="h-28 flex flex-col items-center justify-center space-y-3">
                            <div class="flex items-center space-x-2">
                                <UIcon name="i-ph-file-text-duotone" class="size-4" />
                                <span class="text-sm text-gray-600">{{ state.file.name }}</span>
                            </div>
                            <UButton @click="reset()" size="xs" color="gray"> Remove File </UButton>
                        </div>
                    </div>

                    <UAlert
                        v-if="state.errors?.length"
                        title="There are errors in the selected file"
                        icon="i-ph-warning-duotone"
                        color="red"
                        variant="subtle"
                    >
                        <template #description>
                            <ul class="list-disc pl-6 pt-1">
                                <li v-for="error in state.errors" class="text-xs">
                                    <strong v-if="error.row !== undefined">Row <span class="font-mono">{{ error.row + 2 }}</span>:</strong>
                                    {{ error.message }}
                                </li>
                            </ul>
                        </template>
                    </UAlert>
                    <div v-else-if="state.data.length">
                        <h3 class="mb-0.5 text-sm font-medium text-gray-600">Preview data</h3>
                        <p class="mb-3 text-sm text-gray-500">
                            Here is a preview of the data that will be processed: a total of {{ state.data.length }}.
                        </p>
                        <p v-if="identityMode === 'family' && familySummary" class="mb-3 text-sm text-gray-500">
                            <span class="font-mono font-semibold">{{ familySummary.families }}</span> families detected, the largest with
                            <span class="font-mono font-semibold" :class="{ 'text-red-600': familySummary.largest > LARGE_FAMILY_SIZE }">{{ familySummary.largest }}</span> members.
                            <span v-if="familySummary.withoutPhone" class="text-red-600">
                                <span class="font-mono font-semibold">{{ familySummary.withoutPhone }}</span> without any phone number will be rejected.
                            </span>
                            <span v-if="familySummary.largest > LARGE_FAMILY_SIZE" class="block mt-1 text-red-600">
                                A family this large usually means a corporate policy. Check the file before submitting.
                            </span>
                        </p>
                        <UTable
                            class="border rounded-md min-h-[300px]"
                            :rows="state.data"
                            :columns="previewTableColumns"
                            :ui="{ 
                                th: {
                                    base: 'first:border-0 border-l bg-gray-50',
                                    padding: 'first:pl-3 px-3 py-1.5',
                                    size: 'lowercase'
                                },
                                td: { 
                                    base: 'first:border-l-0 border-l border-b',
                                    padding: 'first:pl-3 px-3 py-2',
                                    size: 'text-sm max-w-[200px] overflow-hidden truncate'
                                }
                            }"
                        />
                    </div>
                </template>
            </div>

            <template #footer v-if="completed || !loading">
                <div class="flex items-center justify-end space-x-2">
                    <UButton v-if="completed" @click="dismiss(true)" size="lg" color="white"> Close </UButton>
                    <template v-else-if="!loading">
                        <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                        <UButton @click="onSubmit()" size="lg" :disabled="!canImport" color="black"> Submit </UButton>
                    </template>
                </div>
            </template>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import Papa from 'papaparse'
import type { Partner } from '~/types/partner'
import { groupFamilies, summarizeFamilies } from '~/supabase/functions/_shared/familyGrouping'
import { UserRole } from '~/types/user'

interface CustomerRow {
    firstName: string
    middleName?: string
    lastName?: string
    phone?: string
    email?: string
    policyHolder?: string
    birthDate: string
    expiresAt?: string
    credentialFields: Record<string, string>
}

// Email-mode (no-phone) activation. Beneficiaries without a phone number get a
// synthetic, non-deliverable email derived from a stable unique credential field
// so they can hold a subscription. No mail is ever sent (the edge function uses
// email_confirm and skips messaging).
const EMAIL_DOMAIN = 'beneficiaries.samu.app'
const EMAIL_ANCHOR_FIELD = 'beneficiary_pin'
const LARGE_FAMILY_SIZE = 12

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    presetPartner?: Partial<Partner>
}>()

const auth = useAuthStore()
const toast = useToast()
const client = useSupabaseClient()

const loading = ref<boolean>(false)
const completed = ref<boolean>(false)
const fileInput = ref<HTMLInputElement>()
const identityMode = ref<'phone' | 'email' | 'family'>('phone')
const familyConfirmed = ref(false)
const familySummary = ref<ReturnType<typeof summarizeFamilies>>()

const state = ref<{
    partner_id?: string
    name?: string
    file?: File
    data: any[]
    errors: Papa.ParseError[]
    meta?: Papa.ParseMeta
}>({
    partner_id: undefined,
    name: undefined,
    file: undefined,
    data: [],
    errors: [],
})

const partner = ref<{
    searching: boolean
    selected?: Partial<Partner>
}>({
    searching: false,
})

const importState = ref<{
    counter: number
    total: number
    existing: number
    invalid: number
    families?: number
    dependents?: number
    errors: Partial<Papa.ParseError>[]
}>({
    counter: 0,
    total: 0,
    existing: 0,
    invalid: 0,
    errors: []
})

const userPartnerId = computed(() => {
    return auth.user?.role === UserRole.partner
        ? auth.user.metadata?.partner_id
        : undefined
})

const quickActivationPartner = computed(() => !!userPartnerId.value && !!partner.value.selected?.metadata?.allow_quick_activation)

// Self-service partners send an expiry per row; the function creates each record
// and activates it in one step instead of matching imported credentials.
const selfService = computed(() => {
    return !!userPartnerId.value
        && identityMode.value === 'phone'
        && !!partner.value.selected?.metadata?.allow_quick_activation
})

const subscriptionFields = computed(() => {
    const allRequired = partner.value.selected?.metadata?.subscription_fields.filter((f) => f.required)
    if (!allRequired) {
        return []
    }

    // Email mode consumes the partner's raw credential columns directly (including
    // birth_year) plus separate name columns. No phone, no full_name, no birth_date.
    if (identityMode.value === 'email') {
        return [
            { key: 'first_name', label: 'First Name', required: true },
            { key: 'middle_name', label: 'Middle Name', required: false },
            { key: 'last_name', label: 'Last Name', required: true },
            ...allRequired,
        ]
    }

    if (identityMode.value === 'family') {
        return [
            { key: 'full_name', label: 'Full Name', required: true },
            { key: 'phone_number', label: 'Phone Number', required: false },
            { key: 'birth_date', label: 'Date of Birth', required: true },
            { key: 'policy_holder', label: 'Policy Holder', required: true },
            ...allRequired.filter((f) => f.key != 'birth_year' && f.key != 'policy_holder'),
        ]
    }

    const requiredFields = allRequired.filter((f) => f.key != 'birth_year')
    return [
        { key: 'full_name', label: 'Full Name', required: true },
        { key: 'phone_number', label: 'Phone Number', required: true },
        { key: 'birth_date', label: 'Date of Birth', required: true },
        ...requiredFields,
        ...(selfService.value ? [{ key: 'expiry_date', label: 'Expiry Date', required: true }] : []),
    ]
})

const previewTableColumns = computed(() => subscriptionFields.value.map((field) => { 
    return { 
        key: field.key, 
        label: field.key + (field.required ? '*' : ''),
        class: 'w-16 overflow-hidden truncate'
    }
}))

const canImport = computed(() => {
    return state.value?.partner_id
        && state.value?.file
        && state.value.data.length
        && !state.value.errors.length
        && (identityMode.value !== 'family' || familyConfirmed.value)
})

watch(identityMode, () => {
    if (state.value.file) {
        reset()
    }
})

watch(() => props.open, async (open) => {
    if (open) {
        reset(true)

        if (props.presetPartner?.id) {
            state.value.partner_id = props.presetPartner.id
            partner.value.selected = props.presetPartner
        } else if (userPartnerId.value) {
            const selectedPartner = await getPartnerById(userPartnerId.value)
            if (selectedPartner) {
                state.value.partner_id = userPartnerId.value
                partner.value.selected = selectedPartner
            }
        }
    }
})

async function searchPartner(q: string) {
    partner.value.searching = true

    const { data, error } = await client
        .from('partners')
        .select('id, name, metadata')
        .is('deleted_at', null)
        .eq('metadata ->> is_public', true)
        .ilike('name', `%${q}%`)

    if (error) {
        return []
    }

    partner.value.searching = false
    return data
}

async function getPartnerById(id: string) {
    const { data } = await client.from('partners')
        .select('id, metadata')
        .is('deleted_at', null)
        .eq('id', id)
        .maybeSingle()

    return data
}

function onChooseFile() {
    if (fileInput.value && fileInput.value.files?.length) {
        state.value.file = fileInput.value.files[0]
        if (state.value.file.size > 5000000) {
            toast.add({
                title: 'File size cannot be larger than 5MB',
                color: 'red',
                icon: 'i-ph-warning-duotone',
            })
            return
        }

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            complete: async (results: Papa.ParseResult<any>) => {
                importState.value.total = results.data.length

                if (identityMode.value === 'family') {
                    familySummary.value = summarizeFamilies(groupFamilies(results.data.map((row: any, index: number) => ({
                        index,
                        policyHolder: row.policy_holder ?? '',
                        fullName: row.full_name ?? '',
                        hasPhone: !!row.phone_number?.toString().trim(),
                    }))))
                }
            },
        })

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            preview: 20,
            complete: async (results: Papa.ParseResult<any>) => {
                const missingRequiredFields = subscriptionFields.value
                    .filter((field) => field.required && !results.meta.fields?.includes(field.key))
                    .map((field) => `'${field.key}'`)

                if (missingRequiredFields?.length) {
                    state.value.errors.push({
                        row: -1,
                        message: 'Header missing required fields: ' + missingRequiredFields.join(', '),
                    } as Papa.ParseError)
                }

                if (results.errors) {
                    state.value.errors.push(...results.errors)
                }

                if (results.data) {
                    state.value.data = results.data
                }
            },
        })
    }
}

async function onSubmit() {
    if (state.value.file && partner.value.selected?.id) {
        loading.value = true

        const data: CustomerRow[] = []

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            chunkSize: 1024 * 64,
            chunk: async (results: Papa.ParseStepResult<any>, parser: Papa.Parser) => {
                parser.pause()
                const rows = await parseRows(results.data)
                data.push(...rows)
                parser.resume()
            },
            complete: async () => {
                await importData(data)
            },
        })
    }
}

async function parseRows(rows: any[]) {
    const selectedPartnerId = partner.value.selected?.id
    if (!selectedPartnerId) {
        throw Error('Partner not selected')
    }

    const requiredFields = partner.value.selected?.metadata?.subscription_fields.filter((f) => f.required)
    if (!requiredFields?.length) {
        throw Error('Invalid partner subscription fields')
    }

    if (identityMode.value === 'email') {
        return parseEmailRows(rows, requiredFields)
    }

    const isFamily = identityMode.value === 'family'
    const parsedRows: CustomerRow[] = []
    for (const row of rows) {
        const { full_name, phone_number, birth_date, expiry_date, ...fields } = row as { [key: string]: string }
        const hasPhone = !!phone_number?.toString().trim()
        if (!full_name || (!hasPhone && !isFamily) || !birth_date || !Object.keys(fields).length) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Invalid row fields' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        if (isFamily && !row.policy_holder?.toString().trim()) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Missing policy holder' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        const phoneNumber = hasPhone ? usePhone().parse(`+${phone_number}`) : undefined
        if (hasPhone && (!phoneNumber?.isValid() || !(phoneNumber.getType() === 'MOBILE' || phoneNumber.getType() === 'FIXED_LINE_OR_MOBILE'))) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Invalid phone number' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        const dateOfBirth = dayjs(birth_date)
        if (!dateOfBirth.isValid()) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Invalid date of birth' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        const expiryDate = dayjs(expiry_date)
        if (selfService.value && (!expiry_date || !expiryDate.isValid() || !expiryDate.isAfter(dayjs(), 'day'))) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Invalid or past expiry date' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        const credentialFields = requiredFields.reduce((acc, { key }) => {
            acc[key] = key === 'birth_year'
                ? dateOfBirth.year().toString()
                : row[key]

            return acc
        }, {} as { [key: string]: string; })

        const { firstName, middleName, lastName } = splitName(full_name)

        parsedRows.push({
            firstName,
            middleName,
            lastName,
            phone: phoneNumber?.number.replaceAll('+', ''),
            policyHolder: isFamily ? row.policy_holder.toString().trim() : undefined,
            birthDate: birth_date,
            expiresAt: selfService.value ? expiryDate.format('YYYY-MM-DD') : undefined,
            credentialFields,
        })
    }

    return parsedRows
}

function parseEmailRows(rows: any[], requiredFields: { key: string }[]) {
    const parsedRows: CustomerRow[] = []
    for (const row of rows) {
        const firstName = (row.first_name || '').trim()
        const lastName = (row.last_name || '').trim()
        const middleName = (row.middle_name || '').trim() || undefined
        const birthYear = (row.birth_year || '').toString().trim()
        const anchor = (row[EMAIL_ANCHOR_FIELD] || '').toString().trim()

        if (!firstName || !lastName || !birthYear || !anchor) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Missing required fields (name, birth_year, or ' + EMAIL_ANCHOR_FIELD + ')' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        if (!/^\d{4}$/.test(birthYear)) {
            importState.value.errors.push({ row: importState.value.counter, message: 'Invalid birth year (expected YYYY)' })
            importState.value.counter++
            importState.value.invalid++
            continue
        }

        const credentialFields = requiredFields.reduce((acc, { key }) => {
            acc[key] = (row[key] ?? '').toString().trim()
            return acc
        }, {} as { [key: string]: string; })

        parsedRows.push({
            firstName,
            middleName,
            lastName,
            email: `${anchor}@${EMAIL_DOMAIN}`.toLowerCase(),
            birthDate: `${birthYear}-01-01`,
            credentialFields,
        })
    }

    return parsedRows
}

async function importData(data: CustomerRow[]) {
    try {
        const skipWhatsApp = data.length >= 500

        const { data: result, error } = await client.functions.invoke('bulk-activation', {
            body: {
                partnerId: partner.value.selected?.id,
                name: state.value.name || null,
                data,
                skipWhatsApp,
                identityType: identityMode.value,
                createCredentials: selfService.value || undefined,
            }
        })

        if (error) throw error

        importState.value.total = result.summary?.total || 0
        importState.value.counter = result.summary?.processed || 0
        importState.value.existing = result.summary?.existing || 0
        importState.value.invalid = result.summary?.invalid || 0
        importState.value.errors = result.errors || []
        importState.value.families = result.summary?.families
        importState.value.dependents = result.summary?.dependents_activated

        completed.value = true

        if (skipWhatsApp) {
            toast.add({
                title: 'Bulk activation completed',
                description: 'WhatsApp messages were skipped for large batch. Use batch details to send later.',
                color: 'blue',
                icon: 'i-ph-info-duotone',
            })
        }
    } catch (error: any) {
        console.error('Bulk activation error:', error)
        toast.add({
            title: 'Failed to process bulk activation',
            description: 'Please try again later.',
            color: 'red',
            icon: 'i-ph-warning-duotone',
        })

        dismiss()
    }

    loading.value = false
}

function splitName(fullName: string): { firstName: string, middleName?: string, lastName?: string } {
    const nameParts = fullName.trim().split(/\s+/)

    if (nameParts.length === 1) {
        return { firstName: nameParts[0] }
    } else if (nameParts.length === 2) {
        return { firstName: nameParts[0], lastName: nameParts[1] }
    } else if (nameParts.length === 3) {
        return { firstName: nameParts[0], middleName: nameParts[1], lastName: nameParts[2] }
    } else {
        return {
            firstName: nameParts[0],
            middleName: nameParts.slice(1, -1).join(' '),
            lastName: nameParts[nameParts.length - 1]
        }
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}

function reset(resetPartner?: boolean) {
    loading.value = false
    completed.value = false
    
    if (resetPartner) {
        partner.value = {
            searching: false,
            selected: undefined
        }
        identityMode.value = 'phone'
    }

    familyConfirmed.value = false
    familySummary.value = undefined

    state.value = {
        partner_id: resetPartner ? undefined : partner.value.selected?.id,
        name: undefined,
        file: undefined,
        data: [],
        errors: [],
        meta: undefined
    }

    importState.value = {
        counter: 0,
        total: 0,
        existing: 0,
        invalid: 0,
        errors: []
    }
}
</script>
